import { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { useTranslation } from 'react-i18next';

import { ROUTES_PATH } from '@/shared/constants/routes';
import type {
  AiDelayedDeliveryDto,
  AiDelayedDeliveryStatusDto,
  AiTraceListItemDto,
  AiTraceReasonCodeDto,
  AiTraceStatusDto,
} from '@/shared/api/ai-trace';
import {
  useAiDelayedDeliveryPreview,
  useAiTracePreview,
} from '../model/useAiAudit';

const PREVIEW_LIMIT = 5;

const aiTraceReasonKeys: Record<AiTraceReasonCodeDto, string> = {
  completed: 'modules.aiAudit.reasons.completed',
  draft_reply_generated: 'modules.aiAudit.reasons.draft_reply_generated',
  autonomous_reply_sent: 'modules.aiAudit.reasons.autonomous_reply_sent',
  autonomous_reply_scheduled:
    'modules.aiAudit.reasons.autonomous_reply_scheduled',
  campaign_draft_generated:
    'modules.aiAudit.reasons.campaign_draft_generated',
  handoff_active: 'modules.aiAudit.reasons.handoff_active',
  module_disabled: 'modules.aiAudit.reasons.module_disabled',
  channel_not_scoped: 'modules.aiAudit.reasons.channel_not_scoped',
  source_not_enabled: 'modules.aiAudit.reasons.source_not_enabled',
  policy_unavailable: 'modules.aiAudit.reasons.policy_unavailable',
  policy_allowed: 'modules.aiAudit.reasons.policy_allowed',
  delay_required: 'modules.aiAudit.reasons.delay_required',
  daily_limit_reached: 'modules.aiAudit.reasons.daily_limit_reached',
  dry_run: 'modules.aiAudit.reasons.dry_run',
  drafts_disabled_by_mode: 'modules.aiAudit.reasons.drafts_disabled_by_mode',
  autonomous_replies_global_disabled:
    'modules.aiAudit.reasons.autonomous_replies_global_disabled',
  draft_reply_not_supported:
    'modules.aiAudit.reasons.draft_reply_not_supported',
  empty_draft: 'modules.aiAudit.reasons.empty_draft',
  producer_unavailable: 'modules.aiAudit.reasons.producer_unavailable',
  delayed_queue_unavailable:
    'modules.aiAudit.reasons.delayed_queue_unavailable',
  invalid_not_before: 'modules.aiAudit.reasons.invalid_not_before',
  duplicate_policy_reservation:
    'modules.aiAudit.reasons.duplicate_policy_reservation',
  request_in_progress: 'modules.aiAudit.reasons.request_in_progress',
  already_processed: 'modules.aiAudit.reasons.already_processed',
  rate_limited: 'modules.aiAudit.reasons.rate_limited',
  provider_failure: 'modules.aiAudit.reasons.provider_failure',
  delayed_delivery_send_now_sent:
    'modules.aiAudit.reasons.delayed_delivery_send_now_sent',
  delayed_delivery_send_now_failed:
    'modules.aiAudit.reasons.delayed_delivery_send_now_failed',
  delayed_delivery_canceled: 'modules.aiAudit.reasons.delayed_delivery_canceled',
  delayed_delivery_cancel_failed:
    'modules.aiAudit.reasons.delayed_delivery_cancel_failed',
  unknown: 'modules.aiAudit.reasons.unknown',
};

function formatDateTime(value: string | null) {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
}

function shortValue(value: string) {
  if (value.length <= 32) return value;

  return `${value.slice(0, 16)}...${value.slice(-8)}`;
}

function getErrorMessage(error: unknown) {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }

  return null;
}

function traceStatusColor(status: AiTraceStatusDto) {
  if (status === 'completed' || status === 'sent') return 'success' as const;
  if (status === 'scheduled' || status === 'drafted') return 'warning' as const;
  if (status === 'failed') return 'error' as const;

  return 'default' as const;
}

function deliveryStatusColor(status: AiDelayedDeliveryStatusDto) {
  if (status === 'sent') return 'success' as const;
  if (status === 'pending' || status === 'processing') return 'warning' as const;
  if (status === 'failed') return 'error' as const;

  return 'default' as const;
}

function traceReasonLabel(
  event: AiTraceListItemDto,
  t: ReturnType<typeof useTranslation>['t']
) {
  return t(aiTraceReasonKeys[event.reason_code], {
    defaultValue: event.reason_code,
  });
}

function TracePreviewList({
  rows,
  isLoading,
}: {
  rows: AiTraceListItemDto[];
  isLoading: boolean;
}) {
  const { t } = useTranslation('company');

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {isLoading
          ? t('modules.aiAudit.loading')
          : t('modules.aiAudit.activity.empty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75}>
      {rows.map((event) => (
        <Stack
          key={event.id}
          spacing={0.5}
          sx={{
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
            '&:first-of-type': { borderTop: 0, pt: 0 },
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="space-between"
            sx={{ minWidth: 0 }}
          >
            <Typography variant="body2" fontWeight={700} noWrap>
              {traceReasonLabel(event, t)}
            </Typography>
            <Chip
              size="small"
              color={traceStatusColor(event.status)}
              label={t(`modules.aiAudit.activity.statuses.${event.status}`, {
                defaultValue: event.status,
              })}
            />
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={t(`modules.aiAudit.activity.modes.${event.mode}`, {
                defaultValue: event.mode,
              })}
            />
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(event.created_at)}
            </Typography>
            {event.chat_id ? (
              <Typography variant="caption" color="text.secondary">
                {shortValue(event.chat_id)}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function DeliveryPreviewList({
  rows,
  isLoading,
}: {
  rows: AiDelayedDeliveryDto[];
  isLoading: boolean;
}) {
  const { t } = useTranslation('company');

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {isLoading
          ? t('modules.aiAudit.loading')
          : t('modules.aiAudit.deliveries.empty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.75}>
      {rows.map((delivery) => (
        <Stack
          key={delivery.id}
          spacing={0.5}
          sx={{
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
            '&:first-of-type': { borderTop: 0, pt: 0 },
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="space-between"
            sx={{ minWidth: 0 }}
          >
            <Typography variant="body2" fontWeight={700} noWrap>
              {delivery.message_preview || shortValue(delivery.message_hash)}
            </Typography>
            <Chip
              size="small"
              color={deliveryStatusColor(delivery.status)}
              label={t(
                `modules.aiAudit.deliveries.statuses.${delivery.status}`,
                { defaultValue: delivery.status }
              )}
            />
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              {t('modules.aiAudit.deliveries.notBefore', {
                value: formatDateTime(delivery.not_before),
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {delivery.last_error ||
                t('modules.aiAudit.deliveries.attempts', {
                  count: delivery.attempt_count,
                })}
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

export function AiAuditPanel({
  companyId,
  accentColor,
  disabled,
}: {
  companyId: string;
  accentColor: string;
  disabled: boolean;
}) {
  const { t } = useTranslation('company');
  const traceFilters = useMemo(
    () => ({ company_id: companyId, limit: PREVIEW_LIMIT }),
    [companyId]
  );
  const deliveryFilters = useMemo(
    () => ({ company_id: companyId, limit: PREVIEW_LIMIT }),
    [companyId]
  );
  const traceQuery = useAiTracePreview(traceFilters);
  const deliveryQuery = useAiDelayedDeliveryPreview(deliveryFilters);
  const traceRows = traceQuery.data?.items ?? [];
  const deliveryRows = deliveryQuery.data?.items ?? [];
  const traceErrorMessage = getErrorMessage(traceQuery.error);
  const deliveryErrorMessage = getErrorMessage(deliveryQuery.error);
  const isFetching = traceQuery.isFetching || deliveryQuery.isFetching;
  const traceUrl = `/${ROUTES_PATH.MAIN.AI_TRACE}?company_id=${encodeURIComponent(
    companyId
  )}`;

  return (
    <Stack
      data-testid="ai-audit-panel"
      spacing={1.5}
      sx={{
        mt: 0.5,
        p: 1.5,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.text.primary, 0.1),
        borderRadius: 1,
        bgcolor: (theme) =>
          alpha(accentColor || theme.palette.primary.main, 0.035),
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={800}>
            {t('modules.aiAudit.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('modules.aiAudit.caption')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.75} alignItems="center">
          <Button
            href={traceUrl}
            size="small"
            variant="contained"
            sx={{
              minHeight: 32,
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 800,
              bgcolor: accentColor,
              '&:hover': { bgcolor: accentColor },
            }}
          >
            {t('modules.aiAudit.openFull')}
          </Button>
          <Tooltip title={t('modules.aiAudit.refresh')} arrow>
            <span>
              <IconButton
                size="small"
                disabled={disabled || isFetching}
                onClick={() => {
                  traceQuery.refetch();
                  deliveryQuery.refetch();
                }}
              >
                <RefreshOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {traceQuery.isError || deliveryQuery.isError ? (
        <Alert severity="error" sx={{ py: 0.5 }}>
          {traceErrorMessage || deliveryErrorMessage || t('modules.aiAudit.error')}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            {t('modules.aiAudit.activity.title')}
          </Typography>
          <TracePreviewList
            rows={traceRows}
            isLoading={traceQuery.isPending}
          />
        </Stack>

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            {t('modules.aiAudit.deliveries.title')}
          </Typography>
          <DeliveryPreviewList
            rows={deliveryRows}
            isLoading={deliveryQuery.isPending}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
